// System Reports picker — same UX as Financial Reports:
// report dropdown + Run, then result table + CSV export.
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import qs from "qs";

import { axios } from "../api";
import { useHasPermission } from "../hooks/useHasPermission";
import { AeronetV6ItemsResponse } from "../types";
import { formatAuditTimestamp } from "../utils/formatAuditTimestamp";

export type SystemReportKey =
  | "parts-searched"
  | "customer-sign-in"
  | "price-download"
  | "system-response";

export const SYSTEM_REPORT_OPTIONS: { key: SystemReportKey; label: string }[] = [
  { key: "parts-searched", label: "Part Searched Report" },
  { key: "customer-sign-in", label: "Customer Sign-In Report" },
  { key: "price-download", label: "Price Download Report" },
  { key: "system-response", label: "System Response Report" },
];

type PartSearchedRow = {
  id?: number;
  part_id?: number;
  part_number?: string | null;
  user_id?: number | null;
  searched_datetime?: string | null;
  company_obj?: { name?: string | null } | null;
};

type CustomerSignInRow = {
  id?: number;
  username?: string | null;
  count?: number;
};

type PriceDownloadRow = {
  id?: number;
  user_name?: string | null;
  count?: number;
  download_datetime?: string | null;
};

type SystemResponseRow = {
  id?: number;
  approved?: number | null;
  quote?: { id?: number } | null;
  part?: { number?: string | null } | null;
};

type AnyRows =
  | PartSearchedRow[]
  | CustomerSignInRow[]
  | PriceDownloadRow[]
  | SystemResponseRow[];

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CsvExportIcon() {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 36,
        height: 36,
        borderRadius: "8px",
        border: "1.5px solid #018a90",
        color: "#018a90",
        fontFamily: '"Hanken Grotesk", system-ui, sans-serif',
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.02em",
        lineHeight: 1,
        backgroundColor: "#fff",
      }}
    >
      CSV
    </Box>
  );
}

function reportToCsv(
  key: SystemReportKey,
  data: AnyRows
): { headers: string[]; rows: (string | number)[][] } {
  switch (key) {
    case "parts-searched": {
      const rows = data as PartSearchedRow[];
      return {
        headers: ["User Id", "Search Date", "Part Number", "Company"],
        rows: rows.map((r) => [
          r.user_id ?? "",
          formatAuditTimestamp(r.searched_datetime, "dd-MM-yyyy"),
          r.part_number ?? "",
          r.company_obj?.name ?? "",
        ]),
      };
    }
    case "customer-sign-in": {
      const rows = data as CustomerSignInRow[];
      return {
        headers: ["Id", "User Name", "Count"],
        rows: rows.map((r) => [r.id ?? "", r.username ?? "", r.count ?? ""]),
      };
    }
    case "price-download": {
      const rows = data as PriceDownloadRow[];
      return {
        headers: ["Count", "User", "Date"],
        rows: rows.map((r) => [
          r.count ?? "",
          r.user_name ?? "",
          formatAuditTimestamp(r.download_datetime, "dd-MM-yyyy"),
        ]),
      };
    }
    case "system-response": {
      const rows = data as SystemResponseRow[];
      return {
        headers: ["Id", "Quote Id", "Part Number", "Approved"],
        rows: rows.map((r) => [
          r.id ?? "",
          r.quote?.id ?? "",
          r.part?.number ?? "",
          r.approved ?? "",
        ]),
      };
    }
    default:
      return { headers: [], rows: [] };
  }
}

async function fetchReport(key: SystemReportKey): Promise<AnyRows> {
  switch (key) {
    case "parts-searched": {
      const resp: AxiosResponse<PartSearchedRow[]> = await axios({
        method: "GET",
        url: "/v6/parts/searched/report/",
        id: "get-parts-searched",
      });
      axios.storage.remove("get-parts-searched");
      return resp.data ?? [];
    }
    case "customer-sign-in": {
      const resp: AxiosResponse<AeronetV6ItemsResponse<CustomerSignInRow>> = await axios({
        method: "GET",
        url: "/v6/users/user_logs/",
        id: "get-customer-signin",
        params: { sort: "-id", size: 15 },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      });
      axios.storage.remove("get-customer-signin");
      return [...(resp.data?.items ?? [])];
    }
    case "price-download": {
      const resp: AxiosResponse<PriceDownloadRow[]> = await axios({
        method: "GET",
        url: "/v6/parts/download_history/",
        id: "get-parts-download_history",
      });
      axios.storage.remove("get-parts-download_history");
      return resp.data ?? [];
    }
    case "system-response": {
      const resp: AxiosResponse<AeronetV6ItemsResponse<SystemResponseRow>> = await axios({
        method: "GET",
        url: "/v6/quotes/lines",
        id: "get-system-response-lines",
        params: { size: 15, sort: "-id" },
        // `arrayFormat: "repeat"` matches the sibling call at :183 and is the
        // form FastAPI binds. NOT a live defect today — these params carry no
        // array, so qs's default never engages — but bare `qs.stringify`
        // brackets-and-INDEXES arrays (`filter[0]=`), so the first person to
        // add a `filter` here would silently get the whole unfiltered table
        // back with a 200. Closing it now costs one argument.
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      });
      axios.storage.remove("get-system-response-lines");
      return [...(resp.data?.items ?? [])];
    }
    default:
      return [];
  }
}

function ResultTable({ reportKey, data }: { reportKey: SystemReportKey; data: AnyRows }) {
  if (reportKey === "parts-searched") {
    const rows = data as PartSearchedRow[];
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User Id</TableCell>
            <TableCell>Search Date</TableCell>
            <TableCell>Part Number</TableCell>
            <TableCell>Company</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id ?? r.part_id ?? i}>
              <TableCell>{r.user_id ?? "—"}</TableCell>
              <TableCell>
                {formatAuditTimestamp(r.searched_datetime, "dd-MM-yyyy") || "—"}
              </TableCell>
              <TableCell>{r.part_number ?? "—"}</TableCell>
              <TableCell>{r.company_obj?.name ?? "—"}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>No rows</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  }

  if (reportKey === "customer-sign-in") {
    const rows = data as CustomerSignInRow[];
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Id</TableCell>
            <TableCell>User Name</TableCell>
            <TableCell align="right">Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id ?? i}>
              <TableCell>{r.id ?? "—"}</TableCell>
              <TableCell>{r.username ?? "—"}</TableCell>
              <TableCell align="right">{r.count ?? "—"}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>No rows</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  }

  if (reportKey === "price-download") {
    const rows = data as PriceDownloadRow[];
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="right">Count</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id ?? i}>
              <TableCell align="right">{r.count ?? "—"}</TableCell>
              <TableCell>{r.user_name ?? "—"}</TableCell>
              <TableCell>
                {formatAuditTimestamp(r.download_datetime, "dd-MM-yyyy") || "—"}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>No rows</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  }

  const rows = data as SystemResponseRow[];
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Id</TableCell>
          <TableCell>Quote Id</TableCell>
          <TableCell>Part Number</TableCell>
          <TableCell>Approved</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={r.id ?? i}>
            <TableCell>{r.id ?? "—"}</TableCell>
            <TableCell>{r.quote?.id ?? "—"}</TableCell>
            <TableCell>{r.part?.number ?? "—"}</TableCell>
            <TableCell>{r.approved ?? "—"}</TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={4}>No rows</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export default function SystemReportsTab() {
  const [reportKey, setReportKey] = useState<SystemReportKey>("parts-searched");
  const [runKey, setRunKey] = useState<SystemReportKey | null>(null);
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<AnyRows | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // GET /v6/users/user_logs/ needs User Management edit (aeronet-fast-api
  // users/api.py, Aaron 2026-10-07): the sign-in report is listed only for a
  // holder, and hidden while the permission is still resolving.
  const canSeeSignIns = useHasPermission("user_management", "edit") === true;
  const options = useMemo(
    () => SYSTEM_REPORT_OPTIONS.filter((o) => o.key !== "customer-sign-in" || canSeeSignIns),
    [canSeeSignIns]
  );

  useEffect(() => {
    if (!runKey) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchReport(runKey)
      .then((rows) => {
        if (!cancelled) {
          setData(rows);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setLoading(false);
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [runKey, nonce]);

  const reportReady = Boolean(runKey && !loading && data);
  const csv = useMemo(
    () => (reportReady && runKey && data ? reportToCsv(runKey, data) : null),
    [reportReady, runKey, data]
  );

  const run = () => {
    setRunKey(reportKey);
    setNonce((n) => n + 1);
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <Box sx={{ flex: "0 1 280px", width: 280, maxWidth: "100%" }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Report"
              value={reportKey}
              onChange={(e) => setReportKey(e.target.value as SystemReportKey)}
              SelectProps={{ sx: { textAlign: "left" } }}
            >
              {options.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 auto", display: "flex", gap: 1, alignItems: "center" }}>
            <Button variant="contained" onClick={run} sx={{ minWidth: 90 }}>
              Run
            </Button>
            {csv && (
              <Tooltip title="Download CSV">
                <IconButton
                  aria-label="Download CSV"
                  onClick={() => downloadCsv(`${runKey}.csv`, csv.headers, csv.rows)}
                  sx={{ p: 0, "&:hover": { backgroundColor: "transparent", opacity: 0.85 } }}
                >
                  <CsvExportIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {!runKey && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Choose a report and click Run.
        </Typography>
      )}
      {runKey && loading && (
        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
      )}
      {runKey && error && (
        <Typography color="error" sx={{ p: 2 }}>
          Failed to run report.
        </Typography>
      )}
      {runKey && !loading && data && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <ResultTable reportKey={runKey} data={data} />
        </Paper>
      )}
    </Box>
  );
}
